<template>
  <div :class="style.tabs">
    <TabPanel
      text="Настройки"
      :active="selectedPanel === 'Настройки'"
      @click="setSelectedPanel('Настройки')"
    />

    <TabPanel
      text="Контент"
      :active="selectedPanel === 'Контент'"
      @click="setSelectedPanel('Контент')"
    />
  </div>

  <div v-if="selectedPanel === 'Настройки'">
    <div :class="style.blockContent">
      <SelectBase
        comment="Тип блока"
        :items="typeBlocks"
        :text="getTypeBlock"
        @select="changeBlockType"
      />
    </div>

    <div v-if="isVerticalType" :class="style.blockContent">
      <span :class="style.heading">Вид блока</span>

      <div
        :class="[
          style.blocks,
          style.typeBlock,
          mobileVersion ? style.mobile : null
        ]"
      >
        <TabStyle
          :icon="singleIcon"
          :iconTone="rightBlock.countImg === 1 ? 'default' : 'tertiary'"
          :active="rightBlock.countImg === 1"
          @click="setCountRightBlocks(1)"
        />

        <TabStyle
          :icon="mobileVersion ? rowsIcon : columnIcon"
          :iconTone="rightBlock.countImg === 2 ? 'default' : 'tertiary'"
          :active="rightBlock.countImg === 2"
          @click="setCountRightBlocks(2)"
        />

        <TabStyle
          v-if="!mobileVersion"
          :icon="gridIcon"
          :iconTone="rightBlock.countImg === 3 ? 'default' : 'tertiary'"
          :active="rightBlock.countImg === 3"
          @click="setCountRightBlocks(3)"
        />
      </div>
    </div>

    <div v-if="isVerticalType" :class="style.blockContent">
      <div :class="style.heightBlock">
        <InputStyle
          description="Высота"
          size="M"
          :suffix="heightSuffix"
          :modelValue="String(normalizedHeight)"
          @update:modelValue="setHeightFromInput"
        />

        <InputUnit>
          <TabStyle
            text="Px"
            :active="heightMeasurement === 'Px'"
            @click="setHeightMeasurement('Px')"
          />

          <TabStyle
            text="%"
            :active="heightMeasurement === '%'"
            @click="setHeightMeasurement('%')"
          />
        </InputUnit>
      </div>
    </div>

    <div :class="style.blockContent">
      <span :class="style.heading">Действия</span>

      <div :class="style.blocks">
        <TooltipComponent position="bottom" text="Переместить выше">
          <SecondaryButton
            :icon="chevronUp"
            :disabled="isMoveUpDisabled"
            @click="moveRightBlockUp"
          />
        </TooltipComponent>

        <TooltipComponent position="bottom" text="Переместить ниже">
          <SecondaryButton
            :icon="chevronDown"
            :disabled="isMoveDownDisabled"
            @click="moveRightBlockDown"
          />
        </TooltipComponent>

        <TooltipComponent position="bottom" text="Дублировать">
          <SecondaryButton :icon="copyIcon" @click="copyRightBlock" />
        </TooltipComponent>

        <TooltipComponent position="bottom" text="Удалить">
          <SecondaryButton :icon="basicTrashIcon" @click="deleteRightBlock" />
        </TooltipComponent>
      </div>
    </div>
  </div>

  <div v-else>
    <div :class="style.blockContent">
      <AccordionBase text="Изображения" showChevronWithActions>
        <template #actions>
          <div />
        </template>

        <div :class="style.inputs">
          <InputFieldMedia
            v-for="imageIndex in visibleImageIndexes"
            :key="imageIndex"
            text="Файл"
            isFile
            :image="getRightSectionImage(imageIndex)"
            :mediaType="getRightSectionMediaType(imageIndex)"
            :actions="getImageActions(imageIndex)"
            @upload="uploadPhotoRightSection(imageIndex)"
          />

          <input
            ref="fileUpload"
            type="file"
            hidden
            accept="image/*, video/*"
            @change="setRightSectionImage"
          />
        </div>
      </AccordionBase>
    </div>

    <div :class="style.blockContent">
      <AccordionBase text="Текст" showChevronWithActions>
        <template #actions>
          <div />
        </template>

        <div :class="style.inputs">
          <InputStyle
            description="Заголовок"
            placeholder="Введите заголовок"
            size="M"
            :modelValue="rightBlock.title ?? ''"
            @update:modelValue="setTitle"
          />

          <InputStyle
            description="Описание"
            placeholder="Введите описание"
            size="M"
            :modelValue="rightBlock.subTitle ?? ''"
            @update:modelValue="setSubTitle"
          />
        </div>
      </AccordionBase>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, markRaw} from "vue";
import style from "./RightSectionSettingComponent.module.scss";
import {DOMAIN} from "@/http";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";
import {
  HeightMeasurement,
  Quiz,
  RightBlockType,
  section
} from "@/page/quiz/typeQuiz";

import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import InputUnit from "@/elements/UI/input/Unit/InputUnit.vue";
import TabPanel from "@/elements/UI/tabs/TabPanel/TabPanel.vue";
import TabStyle from "@/elements/UI/tabs/TabStyle/TabStyle.vue";
import SecondaryButton from "@/elements/UI/button/ButtonStyle/Default/secondary/SecondaryButton.vue";
import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import InputFieldMedia from "@/elements/UI/input/Media/InputFieldMedia.vue";
import SelectBase from "@/elements/UI/select/SelectBase.vue";

import singleIcon from "$images/icons/tabs/Name=Single, Size=16.svg";
import gridIcon from "$images/icons/tabs/Name=Grid, Size=16.svg";
import columnIcon from "$images/icons/tabs/Name=Columns, Size=16.svg";
import rowsIcon from "$images/icons/tabs/Name=Rows, Size=16.svg";

import chevronUp from "$images/icons/button/Name=Chevron-up, Size=16, Tone=Default.svg";
import chevronDown from "$images/icons/button/Name=Chevron-down, Size=16, Tone=Default.svg";
import trashIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import copyIcon from "$images/icons/button/Name=Copy, Size=16, Tone=Default.svg";
import basicTrashIcon from "$images/icons/button/Name=Trash, Size=16, Tone=Default.svg";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";
import TooltipComponent from "@/elements/UI/overlay/Tooltip/TooltipComponent.vue";

type RightSectionImagePayload = {
  file: File;
  sectionIndex: number;
  imageIndex: number;
};

export default defineComponent({
  name: "RightSectionSettingComponent",

  components: {
    TooltipComponent,
    SelectBase,
    InputFieldMedia,
    AccordionBase,
    SecondaryButton,
    InputUnit,
    TabStyle,
    TabPanel,
    InputStyle
  },

  emits: ["closePanel"],

  data() {
    return {
      style,
      DOMAIN,
      modifyQuiz,
      selectedPanel: "Настройки" as SelectedPanel,
      selectedImageIndex: null as number | null,
      newRightSectionImgList: [] as string[],
      cachedPreviewUrls: {} as Record<string, string>,

      typeBlocks: [
        {
          text: "Изображение сверху",
          value: "vertical",
          variant: "button"
        },
        {
          text: "Изображение слева",
          value: "horisontal",
          variant: "button"
        }
      ] as SelectItem[],

      singleIcon,
      gridIcon,
      columnIcon,
      chevronUp,
      chevronDown,
      trashIcon,
      basicTrashIcon,
      rowsIcon,
      copyIcon
    };
  },

  beforeUnmount() {
    this.revokePreviewUrls();
  },

  methods: {
    setSelectedPanel(panel: SelectedPanel): void {
      this.selectedPanel = panel;
    },

    changeBlockType(payload: SelectBasePayload): void {
      const nextType = this.normalizeRightBlockType(payload.value);

      this.setRightBlockField("type", nextType);

      if (nextType === "horisontal") {
        this.setCountRightBlocks(1);
      }
    },

    normalizeRightBlockType(value: unknown): RightBlockType {
      if (value === "horisontal" || value === "horizontal") {
        return "horisontal";
      }

      return "vertical";
    },

    setTitle(value: string): void {
      this.setRightBlockField("title", value);
    },

    setSubTitle(value: string): void {
      this.setRightBlockField("subTitle", value);
    },

    setCountRightBlocks(count: number): void {
      const normalizedCount = this.mobileVersion
        ? Math.max(1, Math.min(count, 2))
        : Math.max(1, Math.min(count, 3));

      this.setRightBlockField("countImg", normalizedCount);
      this.normalizeRightBlockImages(normalizedCount);
    },

    setHeightFromInput(value: string): void {
      this.setHeight(value);
    },

    setHeight(value: string | number): void {
      const height = this.normalizeHeight(value);

      this.setRightBlockField("height", height);
    },

    setHeightMeasurement(value: HeightMeasurement): void {
      this.setRightBlockField("heightMeasurement", value);
    },

    uploadPhotoRightSection(imageIndex: number): void {
      this.selectedImageIndex = imageIndex;

      const refInput = this.$refs.fileUpload as HTMLInputElement | undefined;

      refInput?.click();
    },

    setRightSectionImage(event: Event): void {
      const target = event.target as HTMLInputElement;

      if (this.selectedImageIndex === null) {
        target.value = "";
        return;
      }

      if (!target.files?.[0]) {
        target.value = "";
        return;
      }

      this.uploadRightSectionImage({
        file: target.files[0],
        sectionIndex: this.dataPanel.index,
        imageIndex: this.selectedImageIndex
      });

      this.selectedImageIndex = null;
      target.value = "";
    },

    uploadRightSectionImage(payload: RightSectionImagePayload): void {
      this.ensureRightBlockExists();

      if (!this.quiz?._id) {
        return;
      }

      const fileType = payload.file.name.split(".").pop() || "png";
      const uniquePart = Date.now();

      const newFileName = `${payload.sectionIndex}-${payload.imageIndex}-${uniquePart}-${this.quiz._id}-photoRightSection.${fileType}`;

      const blob = payload.file.slice(0, payload.file.size, payload.file.type);

      const newFile = new File([blob], newFileName, {
        type: payload.file.type
      });

      const previousPreviewUrl =
        this.newRightSectionImgList[payload.imageIndex];

      if (previousPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previousPreviewUrl);
      }

      this.newRightSectionImgList[payload.imageIndex] =
        URL.createObjectURL(newFile);

      const imgList = this.getNormalizedImgList();

      imgList[payload.imageIndex] = newFileName;

      this.modifyQuiz("set", imgList, [
        "rightSectionList",
        payload.sectionIndex,
        "img"
      ]);

      const existingFileIndex = this.cachedFiles.findIndex((file) => {
        const fileData = this.getRightSectionFileData(file.name);

        return (
          fileData !== null &&
          fileData.sectionIndex === payload.sectionIndex &&
          fileData.imageIndex === payload.imageIndex
        );
      });

      if (existingFileIndex !== -1) {
        this.cachedFiles.splice(existingFileIndex, 1, newFile);
      } else {
        this.cachedFiles.push(newFile);
      }

      const cachedPreviewUrl = this.cachedPreviewUrls[newFileName];

      if (cachedPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(cachedPreviewUrl);
      }

      delete this.cachedPreviewUrls[newFileName];
    },

    getRightSectionFileNameParts(fileName: string): {
      sectionIndex: number;
      imageIndex: number;
      tail: string;
      extension: string;
    } | null {
      const baseName =
        String(fileName || "")
          .split("/")
          .pop() || "";

      if (!baseName.includes("photoRightSection")) {
        return null;
      }

      const lastDotIndex = baseName.lastIndexOf(".");
      const nameWithoutExtension =
        lastDotIndex === -1 ? baseName : baseName.slice(0, lastDotIndex);
      const extension =
        lastDotIndex === -1 ? "png" : baseName.slice(lastDotIndex + 1) || "png";
      const nameParts = nameWithoutExtension.split("-");

      const sectionIndex = Number(nameParts[0]);
      const imageIndex = Number(nameParts[1]);

      if (!Number.isFinite(sectionIndex) || !Number.isFinite(imageIndex)) {
        return null;
      }

      const tail =
        nameParts.slice(2).join("-") ||
        `${Date.now()}-${this.quiz?._id || "quiz"}-photoRightSection`;

      return {
        sectionIndex,
        imageIndex,
        tail,
        extension
      };
    },

    getRightSectionFileData(
      fileName: string
    ): {sectionIndex: number; imageIndex: number} | null {
      const fileNameParts = this.getRightSectionFileNameParts(fileName);

      if (!fileNameParts) {
        return null;
      }

      return {
        sectionIndex: fileNameParts.sectionIndex,
        imageIndex: fileNameParts.imageIndex
      };
    },

    getReindexedRightSectionFileName(
      fileName: string,
      sectionIndex: number,
      imageIndex: number
    ): string {
      const fileNameParts = this.getRightSectionFileNameParts(fileName);

      if (!fileNameParts) {
        return fileName;
      }

      return `${sectionIndex}-${imageIndex}-${fileNameParts.tail}.${fileNameParts.extension}`;
    },

    createRenamedRightSectionFile(file: File, fileName: string): File {
      if (file.name === fileName) {
        return file;
      }

      const blob = file.slice(0, file.size, file.type);

      return new File([blob], fileName, {
        type: file.type
      });
    },

    normalizePendingRightSectionFiles(rightSectionList: section[]): section[] {
      const cachedRightSectionFiles = new Map<string, File>();

      this.cachedFiles.forEach((file) => {
        if (this.getRightSectionFileNameParts(file.name)) {
          cachedRightSectionFiles.set(file.name, file);
        }
      });

      if (cachedRightSectionFiles.size === 0) {
        return rightSectionList;
      }

      const usedPendingFileNames = new Set<string>();
      const affectedOldFileNames = new Set<string>();
      const nextFiles: File[] = [];

      const normalizedList = rightSectionList.map(
        (rightSection, sectionIndex) => {
          const imgList = Array.isArray(rightSection.img)
            ? [...rightSection.img]
            : typeof rightSection.img === "string" && rightSection.img
              ? [rightSection.img]
              : [];

          let isChanged = false;

          const normalizedImgList = imgList.map((imageName, imageIndex) => {
            const cachedFile = cachedRightSectionFiles.get(imageName);

            if (!cachedFile) {
              return imageName;
            }

            const nextFileName = this.getReindexedRightSectionFileName(
              imageName,
              sectionIndex,
              imageIndex
            );

            usedPendingFileNames.add(nextFileName);
            affectedOldFileNames.add(cachedFile.name);
            nextFiles.push(
              this.createRenamedRightSectionFile(cachedFile, nextFileName)
            );

            if (nextFileName !== imageName) {
              isChanged = true;
            }

            return nextFileName;
          });

          if (!isChanged) {
            return rightSection;
          }

          return {
            ...rightSection,
            img: normalizedImgList
          } as section;
        }
      );

      const nextFileNames = new Set(nextFiles.map((file) => file.name));

      for (let index = this.cachedFiles.length - 1; index >= 0; index--) {
        const file = this.cachedFiles[index];

        if (!this.getRightSectionFileNameParts(file.name)) {
          continue;
        }

        if (
          affectedOldFileNames.has(file.name) ||
          nextFileNames.has(file.name) ||
          !usedPendingFileNames.has(file.name)
        ) {
          this.cachedFiles.splice(index, 1);
        }
      }

      nextFiles.forEach((file) => {
        this.cachedFiles.push(file);
      });

      return normalizedList;
    },

    getRightSectionImage(imageIndex: number): string {
      const previewUrl = this.newRightSectionImgList[imageIndex];

      if (previewUrl) {
        return `url('${previewUrl}')`;
      }

      const path = this.rightBlockImages[imageIndex];

      if (!path) {
        return "";
      }

      if (path.startsWith("blob:")) {
        return `url('${path}')`;
      }

      if (path.startsWith("http://") || path.startsWith("https://")) {
        return `url('${path}')`;
      }

      const cachedPreviewUrl = this.getCachedFilePreviewUrl(path);

      if (cachedPreviewUrl) {
        return `url('${cachedPreviewUrl}')`;
      }

      return `url('https://${this.DOMAIN}/api/img?name=${path}')`;
    },

    getRightSectionMediaType(imageIndex: number): "image" | "video" {
      return this.isVideoPath(this.rightBlockImages[imageIndex])
        ? "video"
        : "image";
    },

    isVideoPath(path = ""): boolean {
      return /\.(mp4|webm|ogg)(?:$|[?#])/i.test(path);
    },

    getCachedFilePreviewUrl(fileName: string): string {
      const cachedFile = this.cachedFiles.find((file) => {
        return file.name === fileName;
      });

      if (!cachedFile) {
        return "";
      }

      const currentPreviewUrl = this.cachedPreviewUrls[fileName];

      if (currentPreviewUrl) {
        return currentPreviewUrl;
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.cachedPreviewUrls = {
        ...this.cachedPreviewUrls,
        [fileName]: previewUrl
      };

      return previewUrl;
    },

    getImageActions(imageIndex: number): ListActionItem[] {
      return [
        {
          text: "Удалить",
          icon: trashIcon,
          handler: () => this.deleteRightSectionImage(imageIndex),
          variant: "button",
          buttonComponent: markRaw(GhostButtonError)
        }
      ];
    },

    removeCachedRightSectionImage(
      imageName: string,
      sectionIndex: number,
      imageIndex: number
    ): void {
      for (let index = this.cachedFiles.length - 1; index >= 0; index--) {
        const file = this.cachedFiles[index];
        const fileData = this.getRightSectionFileData(file.name);

        const isSameFileName = Boolean(imageName) && file.name === imageName;

        const isSameRightSectionSlot =
          fileData !== null &&
          fileData.sectionIndex === sectionIndex &&
          fileData.imageIndex === imageIndex;

        if (isSameFileName || isSameRightSectionSlot) {
          this.cachedFiles.splice(index, 1);
        }
      }
    },

    deleteRightSectionImage(imageIndex: number): void {
      if (imageIndex < 0) {
        return;
      }

      const imgList = this.getNormalizedImgList();
      const removedImageName = imgList[imageIndex] || "";
      const removedPreviewUrl = this.newRightSectionImgList[imageIndex];

      if (!removedImageName && !removedPreviewUrl) {
        return;
      }

      if (removedPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(removedPreviewUrl);
      }

      this.newRightSectionImgList.splice(imageIndex, 1);

      if (imageIndex < imgList.length) {
        imgList.splice(imageIndex, 1);
      }

      this.removeCachedRightSectionImage(
        removedImageName,
        this.dataPanel.index,
        imageIndex
      );

      Object.values(this.cachedPreviewUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      this.cachedPreviewUrls = {};

      const nextRightSectionList = [...this.rightSectionList];

      const currentRightBlock =
        nextRightSectionList[this.dataPanel.index] ||
        this.createDefaultRightBlock();

      nextRightSectionList[this.dataPanel.index] = {
        ...currentRightBlock,
        img: imgList
      } as section;

      const normalizedList =
        this.normalizePendingRightSectionFiles(nextRightSectionList);

      this.modifyQuiz("set", normalizedList, ["rightSectionList"]);
    },

    getNormalizedImgList(): string[] {
      const img = this.rightBlock.img;

      if (Array.isArray(img)) {
        return [...img];
      }

      if (typeof img === "string" && img) {
        return [img];
      }

      return [];
    },

    normalizeRightBlockImages(count: number): void {
      const imgList = this.getNormalizedImgList();

      while (imgList.length < count) {
        imgList.push("");
      }

      if (imgList.length > count) {
        const removedPreviews = this.newRightSectionImgList.splice(count);

        removedPreviews.forEach((url) => {
          if (url?.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });

        imgList.splice(count);
      }

      this.modifyQuiz("set", imgList, [
        "rightSectionList",
        this.dataPanel.index,
        "img"
      ]);
    },

    moveRightBlockUp(): void {
      if (this.isMoveUpDisabled) {
        return;
      }

      const rightSectionList = [...this.rightSectionList];

      const currentBlock = rightSectionList[this.dataPanel.index];
      const previousBlock = rightSectionList[this.dataPanel.index - 1];

      rightSectionList[this.dataPanel.index - 1] = currentBlock;
      rightSectionList[this.dataPanel.index] = previousBlock;

      const normalizedList =
        this.normalizePendingRightSectionFiles(rightSectionList);

      this.modifyQuiz("set", normalizedList, ["rightSectionList"]);

      this.dataPanel.index -= 1;
    },

    moveRightBlockDown(): void {
      if (this.isMoveDownDisabled) {
        return;
      }

      const rightSectionList = [...this.rightSectionList];

      const currentBlock = rightSectionList[this.dataPanel.index];
      const nextBlock = rightSectionList[this.dataPanel.index + 1];

      rightSectionList[this.dataPanel.index + 1] = currentBlock;
      rightSectionList[this.dataPanel.index] = nextBlock;

      const normalizedList =
        this.normalizePendingRightSectionFiles(rightSectionList);

      this.modifyQuiz("set", normalizedList, ["rightSectionList"]);

      this.dataPanel.index += 1;
    },

    copyRightBlock(): void {
      const sourceBlock = this.rightSectionList[this.dataPanel.index];

      if (!sourceBlock) {
        return;
      }

      const rightSectionList = [...this.rightSectionList];
      const copiedBlock = {
        ...this.createDefaultRightBlock(),
        ...sourceBlock,
        img: Array.isArray(sourceBlock.img)
          ? [...sourceBlock.img]
          : typeof sourceBlock.img === "string" && sourceBlock.img
            ? [sourceBlock.img]
            : [""],
        positioningImg: Array.isArray(sourceBlock.positioningImg)
          ? [...sourceBlock.positioningImg]
          : sourceBlock.positioningImg
      } as section;
      const insertIndex = this.dataPanel.index + 1;

      rightSectionList.splice(insertIndex, 0, copiedBlock);

      const normalizedList =
        this.normalizePendingRightSectionFiles(rightSectionList);

      this.modifyQuiz("set", normalizedList, ["rightSectionList"]);
      this.dataPanel.index = insertIndex;
    },

    deleteRightBlock(): void {
      const rightSectionList = [...this.rightSectionList];

      rightSectionList.splice(this.dataPanel.index, 1);

      const normalizedList =
        this.normalizePendingRightSectionFiles(rightSectionList);

      this.modifyQuiz("set", normalizedList, ["rightSectionList"]);
      this.$emit("closePanel");
    },

    setRightBlockField<Field extends keyof section>(
      field: Field,
      value: section[Field]
    ): void {
      this.ensureRightBlockExists();

      this.modifyQuiz("set", value, [
        "rightSectionList",
        this.dataPanel.index,
        field
      ]);
    },

    ensureRightBlockExists(): void {
      if (this.rightSectionList[this.dataPanel.index]) {
        return;
      }

      const rightSectionList = [...this.rightSectionList];

      rightSectionList[this.dataPanel.index] = this.createDefaultRightBlock();

      this.modifyQuiz("set", rightSectionList, ["rightSectionList"]);
    },

    createDefaultRightBlock(): section {
      return {
        img: [""],
        title: "",
        subTitle: "",
        countImg: 1,
        height: 100,
        heightMeasurement: "%",
        type: "vertical",
        positioningImg: "center"
      } as section;
    },

    normalizeHeight(value: string | number): number {
      const numberValue = Number(value);

      if (Number.isNaN(numberValue)) {
        return 100;
      }

      return numberValue;
    },

    normalizeHeightMeasurement(value: unknown) {
      if (value === "PX" || value === "px" || value === "Px") {
        return "Px";
      }

      return "%";
    },

    revokePreviewUrls(): void {
      this.newRightSectionImgList.forEach((url) => {
        if (url?.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      Object.values(this.cachedPreviewUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      this.cachedPreviewUrls = {};
    }
  },

  computed: {
    quiz(): Quiz {
      return this.$quizStore.getQuiz;
    },

    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles;
    },

    rightSectionList(): section[] {
      const value = this.modifyQuiz("get", "", ["rightSectionList"]);

      return Array.isArray(value) ? value : [];
    },

    rightBlock(): section {
      const block = this.rightSectionList[this.dataPanel.index];

      if (block) {
        return {
          ...this.createDefaultRightBlock(),
          ...block,
          img: Array.isArray(block.img)
            ? block.img
            : typeof block.img === "string" && block.img
              ? [block.img]
              : [""],
          height: block.height ?? 100,
          heightMeasurement: block.heightMeasurement ?? "%",
          type: this.normalizeRightBlockType(block.type)
        } as section;
      }

      return this.createDefaultRightBlock();
    },

    getTypeBlock(): string {
      const currentType = this.normalizeRightBlockType(this.rightBlock.type);

      const currentItem = this.typeBlocks.find((item) => {
        return item.value === currentType;
      });

      return currentItem?.text ?? "Вертикальный";
    },

    isVerticalType(): boolean {
      return this.normalizeRightBlockType(this.rightBlock.type) === "vertical";
    },

    rightBlockImages(): string[] {
      const img = this.rightBlock.img;

      if (Array.isArray(img)) {
        return img;
      }

      if (typeof img === "string" && img) {
        return [img];
      }

      return [];
    },

    visibleImageIndexes(): number[] {
      if (!this.isVerticalType) {
        return [0];
      }

      const countImg = Number(this.rightBlock.countImg || 1);
      let normalizedCount = this.mobileVersion
        ? Math.max(1, Math.min(countImg, 2))
        : Math.max(1, Math.min(countImg, 3));

      if (normalizedCount === 3) {
        normalizedCount++;
      }

      return Array.from({length: normalizedCount}, (_item, index) => index);
    },

    normalizedHeight(): number {
      return this.normalizeHeight(this.rightBlock.height ?? 100);
    },

    heightMeasurement(): HeightMeasurement {
      return this.normalizeHeightMeasurement(this.rightBlock.heightMeasurement);
    },

    heightSuffix(): string {
      return this.heightMeasurement;
    },

    isMoveUpDisabled(): boolean {
      return this.dataPanel.index <= 0;
    },

    isMoveDownDisabled(): boolean {
      return this.dataPanel.index >= this.rightSectionList.length - 1;
    },

    selectedVersion(): string {
      return this.$quizStore.getSelectedVersion;
    },

    mobileVersion(): boolean {
      return this.selectedVersion === "Мобильная";
    },

    dataPanel() {
      return this.$panelStore.getDataPanelClues;
    }
  },

  watch: {
    cachedFiles(): void {
      Object.values(this.cachedPreviewUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      this.cachedPreviewUrls = {};
    }
  }
});
</script>
